import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { FormValidator } from 'src/app/validators/form-validator';
import { CustomFormControlAccessWithLoaders } from 'src/app/_core/tools/custom-form-control-access';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { MutationIsMotDePasseDifferentArgs } from 'src/graphql/generated-types';
import gql from 'graphql-tag';

type EditPasswordFormType = {
    oldPassword: FormControl<string | null>;
    newPassword: FormControl<string | null>;
    confirmPassword: FormControl<string | null>;
}

export type EditPasswordFormValueType = {
    oldPassword: string | null;
    newPassword: string | null;
    confirmPassword: string | null
};

@Component({
    selector: 'app-shared-edit-password',
    templateUrl: './shared-edit-password.component.html',
    styleUrls: ['./shared-edit-password.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedEditPasswordComponent),
    standalone: false
})
export class SharedEditPasswordComponent extends CustomFormControlAccessWithLoaders implements OnInit {

    form: FormGroup<EditPasswordFormType>;

    @Input()
    @Coerce('boolean')
    isThemeReverse = false;

    newPasswordErrors = {
        passwordMinLength: {label: '14 caractères', icon: 'close'},
        passwordNeedUppercaseLetter: {label: '1 majuscule [A-Z]', icon: 'close'},
        passwordNeedLowercaseLetter: {label: '1 minuscule [a-z]', icon: 'close'},
        passwordNeedNumber: {label: '1 chiffre [0-9]', icon: 'close'},
        passwordNoSpecialCharacter: {
            label: '1 caractère spécial parmi : @ # $ £ ¤ § % ^ & * ( )  _ + – = { } [ ] | : ; , . ? ! \\ / < > ¨ µ ~`²',
            icon: 'close'
        }
    };

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                private readonly fb: FormBuilder) {
        super(cdr);
    }

    validate(c: AbstractControl): Observable<{invalidinformations: boolean} | null> {
        return of(this.form.invalid ? {invalidinformations: true} : null);
    }

    get theme(): string {
        return this.isThemeReverse ? 'reverse' : '';
    }

    private isSamePasswordAsCurrentAsyncValidator$(): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => this.wrapLoader$(this.isCurrentPasswordDifferent$(control.value).pipe(
            map(arePasswordsDifferent => arePasswordsDifferent
                ? {isnotsameascurrentpassword: true}
                : null)
        ), 'isSamePasswordAsCurrent');
    }

    /**
     * Vérifie si le mot de passe actuel renseigné est différent du vrai mot de passe actuel
     * @param password - Mot de passe actuel à renseigner
     * @private
     */
    private isCurrentPasswordDifferent$(password: string): Observable<boolean> {
        return this.graphqlService.mutateOne<'IsMotDePasseDifferent', MutationIsMotDePasseDifferentArgs>({
            mutation: gql`
                mutation IsMotDePasseDifferent($contexte: ContextInfosInput, $password: String) {
                    IsMotDePasseDifferent(contexte: $contexte, password: $password)
                }
            `,
            variables: { password }
        })
    }

    ngOnInit(): void {
        this.form = this.fb.group<EditPasswordFormType>({
            oldPassword: this.fb.control(null, {
                validators: [FormValidator.isNotNull],
                asyncValidators: [this.isSamePasswordAsCurrentAsyncValidator$()],
                updateOn: 'blur'
            }),
            newPassword: this.fb.control(null, [FormValidator.isNotNull, FormValidator.passwordValidator]),
            confirmPassword: this.fb.control(null, [FormValidator.isNotNull]),
        }, {
            validators: [FormValidator.passwordRepeat('newPassword', 'confirmPassword')],
        });

        this.watch$ = this.form.valueChanges.pipe(
            tap(formValues => this.notifyUpdate(formValues))
        );

        this.watch$ = this.form.get('newPassword').valueChanges.pipe(
            tap(newPassword => {
                this.newPasswordErrors.passwordMinLength.icon = newPassword.trim().length >= 14 ? 'check' : 'close';
                this.newPasswordErrors.passwordNeedLowercaseLetter.icon =
                    newPassword.toLocaleUpperCase() !== newPassword ? 'check' : 'close';
                this.newPasswordErrors.passwordNeedUppercaseLetter.icon =
                    newPassword.toLocaleLowerCase() !== newPassword ? 'check' : 'close';
                this.newPasswordErrors.passwordNeedNumber.icon = /\d/.test(newPassword) ? 'check' : 'close';
                this.newPasswordErrors.passwordNoSpecialCharacter.icon =
                    /[@#$£¤§%^&*()_+–={}\[\]|:;,.?!\\/<>¨µ~`²]/.test(newPassword) ? 'check' : 'close';
            })
        );
    }
}
